import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, PiggyBank, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { queryClient, trpc, trpcClient } from '@/utils/trpc';
import { BudgetCreator } from './budget-creator';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

export function BudgetManagementSimple() {
  // Fetch data
  const budgets = useQuery(
    trpc.budgets.list.queryOptions({ period: 'monthly' })
  );

  // Delete budget mutation
  const deleteBudget = useMutation({
    mutationFn: (budgetId: string) =>
      trpcClient.budgets.delete.mutate({ id: budgetId }),
    onSuccess: () => {
      budgets.refetch();
      queryClient.invalidateQueries({
        queryKey: ['budgets', 'list', { period: 'monthly' }],
      });
    },
    onError: (error: any) => {
      console.error('Erro ao deletar orçamento:', error.message);
      alert(`Erro ao deletar orçamento: ${error.message}`);
    },
  });

  const handleDeleteBudget = (budgetId: string, budgetName: string) => {
    const confirmed = confirm(
      `Tem certeza que deseja deletar o orçamento "${budgetName}"?`
    );
    if (confirmed) {
      deleteBudget.mutate(budgetId);
    }
  };

  if (budgets.isPending) {
    return (
      <div className="space-y-6">
        <div className="h-8 animate-pulse rounded bg-muted" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div className="h-32 animate-pulse rounded bg-muted" key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (budgets.error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600">
            Erro ao carregar orçamentos: {budgets.error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const budgetData = budgets.data || [];

  return (
    <div className="space-y-6">
      {/* Budget Creation */}
      <BudgetCreator />

      {/* Budget List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="h-5 w-5" />
            Orçamentos Ativos ({budgetData.length})
          </CardTitle>
          <CardDescription>
            Seus orçamentos mensais e progresso atual
          </CardDescription>
        </CardHeader>
        <CardContent>
          {budgetData.length === 0 ? (
            <div className="py-12 text-center">
              <PiggyBank className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 font-semibold text-lg">
                Nenhum orçamento encontrado
              </h3>
              <p className="mb-4 text-muted-foreground">
                Crie categorias e configure orçamentos para começar a controlar
                seus gastos mensais
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {budgetData.map((budget: any) => (
                <div className="rounded-lg border p-4" key={budget.id}>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {budget.category ? (
                          <>
                            <span className="text-lg">
                              {budget.category.icon}
                            </span>
                            <span className="font-medium">
                              {budget.category.name}
                            </span>
                          </>
                        ) : (
                          <>
                            <PiggyBank className="h-5 w-5 text-blue-600" />
                            <span className="font-medium">Orçamento Geral</span>
                          </>
                        )}
                      </div>
                      <Badge className="text-xs" variant="outline">
                        Mensal
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="text-xs" variant="outline">
                        ID: {budget.id.slice(-6)}
                      </Badge>
                      <Button
                        className="h-6 w-6 p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        disabled={deleteBudget.isPending}
                        onClick={() =>
                          handleDeleteBudget(
                            budget.id,
                            budget.category?.name || 'Orçamento Geral'
                          )
                        }
                        size="sm"
                        variant="ghost"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <span>
                          <span className="font-medium">Gasto:</span>{' '}
                          {formatCurrency(budget.spent || 0)}
                        </span>
                        <span>
                          <span className="font-medium">Orçado:</span>{' '}
                          {formatCurrency(Number(budget.amount))}
                        </span>
                        <span>
                          <span className="font-medium">Restante:</span>
                          <span
                            className={
                              (budget.remaining || 0) < 0
                                ? 'text-red-600'
                                : 'text-green-600'
                            }
                          >
                            {formatCurrency(
                              budget.remaining || Number(budget.amount)
                            )}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={
                            budget.isOverBudget
                              ? 'destructive'
                              : budget.isNearLimit
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {budget.percentage || 0}%
                        </Badge>
                        {budget.isOverBudget ? (
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                        ) : budget.isNearLimit ? (
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                    </div>
                    <Progress
                      className={`h-2 ${
                        budget.isOverBudget
                          ? '[&>*]:bg-red-500'
                          : budget.isNearLimit
                            ? '[&>*]:bg-yellow-500'
                            : '[&>*]:bg-green-500'
                      }`}
                      value={Math.min(budget.percentage || 0, 100)}
                    />
                    <div className="flex items-center justify-between text-muted-foreground text-xs">
                      <span>{budget.transactionCount || 0} transações</span>
                      <span>
                        {budget.isOverBudget &&
                          `⚠️ Estourou em ${formatCurrency(
                            (budget.spent || 0) - Number(budget.amount)
                          )}`}
                        {budget.isNearLimit &&
                          !budget.isOverBudget &&
                          `🔔 Próximo do limite`}
                        {!budget.isOverBudget &&
                          !budget.isNearLimit &&
                          '✅ Dentro do orçamento'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
